import { Children, Fragment, type ReactElement, type ReactNode } from 'react'

import cn from 'clsx'
import { Accordion as MarketistAccordion } from '@/components/Accordion/Accordion'
import { Box } from '@/components/Box/Box'
import { AccordionItem as FaqItem } from '@/components/Accordion/Accordion'
import type { Themes } from '@/Theme'

import css from './Accordion.module.css'

import type { BoxProps } from '@/components/Box/Box'

interface AccordionProps extends BoxProps {
    theme: Themes
}

export function HastAccordion({ theme, children, className, ...rest }: AccordionProps) {
    const faqItems: FaqItem[] = []

    /** Transforms HTML tree into <FAQ /> props. */
    let currentAnswer: ReactNode[] = []

    function isAccordionTitle(child: ReactElement) {
        // If the tag is h4, it's a title by default.
        // Otherwise, it's a title if it has a `data-accordion-title` attribute.

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return typeof child.props['data-accordion-title'] === 'string' || child.type.name === 'h4'
    }

    Children.forEach(children, (child) => {
        /** Remove empty child. */
        if (!child) return

        if (typeof child === 'object' && 'props' in child && isAccordionTitle(child)) {
            if (faqItems.length > 0) {
                const last = faqItems[faqItems.length - 1]
                // biome-ignore lint/complexity/noUselessFragments: required to change type
                last.answer = <Fragment>{currentAnswer}</Fragment>
                currentAnswer = []
            }

            const item = new FaqItem(child, <span />)
            faqItems.push(item)
        } else {
            currentAnswer.push(child)
        }
    })

    if (faqItems.length > 0) {
        const lastItem = faqItems[faqItems.length - 1]
        // biome-ignore lint/complexity/noUselessFragments: required to change type
        lastItem.answer = <Fragment>{currentAnswer}</Fragment>
    }
    return (
        <Box className={cn(css.accordion, className)} {...rest}>
            <MarketistAccordion useAsTemplate items={faqItems} />
        </Box>
    )
}

export function Accordion({ theme, children, className, ...rest }: AccordionProps) {
    const faqItems: FaqItem[] = []

    /** Transforms HTML tree into <FAQ /> props. */
    let currentAnswer: ReactNode[] = []

    function isAccordionTitle(props: Record<string, string>) {
        // If the tag is h4, it's a title by default.
        // Otherwise, it's a title if it has a `data-accordion-title` attribute.
        return typeof props['data-accordion-title'] === 'string' || props.tag === 'h4'
    }

    Children.forEach(children, (child) => {
        /** Remove empty child. */
        if (!child) return

        if (typeof child === 'object' && 'props' in child && isAccordionTitle(child.props)) {
            if (faqItems.length > 0) {
                const last = faqItems[faqItems.length - 1]
                // biome-ignore lint/complexity/noUselessFragments: required to change type
                last.answer = <Fragment>{currentAnswer}</Fragment>
                currentAnswer = []
            }

            const item = new FaqItem(child, <span />)
            faqItems.push(item)
        } else {
            currentAnswer.push(child)
        }
    })

    if (faqItems.length > 0) {
        const lastItem = faqItems[faqItems.length - 1]
        // biome-ignore lint/complexity/noUselessFragments: required to change type
        lastItem.answer = <Fragment>{currentAnswer}</Fragment>
    }

    return (
        <Box className={cn(css.accordion, className)} {...rest}>
            <MarketistAccordion useAsTemplate items={faqItems} />
        </Box>
    )
}
